import { computed, ref, watch } from 'vue'
import { api, endpoints } from '../../services/api.js'
import { formationsCatalogueService } from '../../services/formationsCatalogueService.js'
import { useFormateursCarte } from '../formateurs/useFormateursCarte.js'
import { useCompetencesReferentiel } from '../formateurs/useCompetencesReferentiel.js'
import { lieuDeReference } from '../../utils/lieuDeReferenceDevis.js'
import { resumeDeLaCarte } from '../../utils/resumeCarteFormateurs.js'
import { normaliserRecherche } from '../../utils/conciergerie.js'
import { RAYON_DEFAUT_KM } from '../../constants/formateurCiblage.js'

/**
 * LA CARTE DES FORMATEURS DU POINT DE VIGILANCE (retour Figma du 2026-10-05) :
 * la carte de la liste des formateurs, remplie d'office pour la demande. Le
 * commercial y voit s'il y a un formateur près du lieu — et sinon, qu'il faudra
 * compter le déplacement dans le prix du devis.
 *
 * REMPLIE D'OFFICE :
 *  — compétences : celles que le catalogue donne à la formation. Quand il y en
 *    a, le serveur ne regarde plus la catégorie (cf. `parametresFiltres`) ;
 *  — catégorie : celle de la formation, retrouvée par son nom dans le
 *    référentiel quand la formation n'a pas de compétences ;
 *  — statut, type, réseau : tous ;
 *  — centre : le lieu de la formation (cf. `lieuDeReference`).
 * Seule la distance se règle, comme sur la carte ordinaire.
 *
 * MÊME QUESTION AU SERVEUR QUE LA CARTE ORDINAIRE (`useFormateursCarte`) : rien
 * n'est refiltré ici.
 *
 * @param {import('vue').Ref<Object|null>} quote - La demande affichée.
 * @param {import('vue').Ref<Object|null>} selectedFormation - Formation du catalogue.
 */
export function useBesoinCarteFormateurs(quote, selectedFormation) {
  const { categories, loadCompetencesReferentiel, getCategoryLabel, getSousCategorieLabel } = useCompetencesReferentiel()
  const filters = ref({ statut: '', type: '', reseau: '', categorie: '', competences: [], point: null, rayonKm: RAYON_DEFAUT_KM })
  const lieu = ref(null)
  /** 'preparation' | 'sans-lieu' | 'introuvable' | 'prete' */
  const etat = ref('preparation')

  const carte = useFormateursCarte(filters, () => Boolean(filters.value.point))

  const critere = computed(() => {
    const { competences, categorie } = filters.value
    if (competences.length) {
      return competences.map((c) => `${getCategoryLabel(c.categorie)} › ${getSousCategorieLabel(c.categorie, c.sousCategorie)}`).join(', ')
    }
    return categorie ? getCategoryLabel(categorie) : null
  })

  async function preparer() {
    etat.value = 'preparation'
    lieu.value = lieuDeReference(quote.value)
    if (!lieu.value) {
      etat.value = 'sans-lieu'
      return
    }
    const [competences, point] = await Promise.all([competencesDeLaFormation(), resoudre(lieu.value.recherche)])
    if (!point) {
      etat.value = 'introuvable'
      return
    }
    const categorie = competences.length ? '' : await categorieDeLaFormation()
    filters.value = { ...filters.value, competences, categorie, point }
    etat.value = 'prete'
  }

  async function competencesDeLaFormation() {
    const id = selectedFormation.value?.ID
    if (!id) return []
    try {
      const { competences } = await formationsCatalogueService.getCompetences(id)
      return competences || []
    } catch {
      return []
    }
  }

  async function categorieDeLaFormation() {
    await loadCompetencesReferentiel().catch(() => {})
    const cherchee = normaliserRecherche(quote.value?.formation_categorie)
    const trouvee = cherchee && categories.value.find(
      (c) => normaliserRecherche(c.label) === cherchee || normaliserRecherche(c.id) === cherchee
    )
    return trouvee ? trouvee.id : ''
  }

  async function resoudre(recherche) {
    try {
      return await api.get(endpoints.formateurs.cartePoint(recherche))
    } catch {
      return null
    }
  }

  const setRayon = (km) => { filters.value = { ...filters.value, rayonKm: km } }

  // Le rayon de la DERNIÈRE RÉPONSE, et non celui du curseur : pendant le délai
  // avant rechargement, la liste en mémoire est encore celle de l'ancien rayon.
  const rayonDemande = ref(null)
  const rayonRepondu = ref(null)
  watch(carte.loading, (enCours) => {
    if (enCours) rayonDemande.value = filters.value.rayonKm
    else if (!carte.error.value) rayonRepondu.value = rayonDemande.value
  })

  const resume = computed(() => resumeDeLaCarte({
    formateurs: carte.formateurs.value,
    rayon: rayonRepondu.value,
    aJour: etat.value === 'prete' && !carte.loading.value && rayonRepondu.value === filters.value.rayonKm
  }))

  return { ...carte, filters, lieu, etat, critere, resume, preparer, setRayon }
}
